import React from 'react';

import { MenuTile } from 'features/menu/components/menu-tile-grid/menu-tile';
import { MenuTileGridHeader } from 'features/menu/components/menu-tile-grid/menu-tile-grid-header';
import {
  AnyMenuObject,
  getCaloriesAndPrice,
} from 'features/menu/components/menu-tile-grid/menu-tile/helpers';
import { useCalories } from 'features/menu/components/menu-tile-grid/menu-tile/hooks';
import { MainMenuObject } from 'features/menu/components/menu-tile-grid/menu-tile/types';
import { Separator } from 'features/menu/components/menu-tile-grid/separator';
import {
  StyledItemPrice,
  StyledListItemCalories,
  StyledMenuSectionTitle,
  StyledPriceAndCaloriesListWrapper,
  TileCard,
  TileFooter,
  TileImage,
} from 'features/menu/components/menu-tile-grid/styled';
import { IMenuTileGridProps } from 'features/menu/components/menu-tile-grid/types';
import { useGetVariantContentOptions } from 'features/menu/hooks/use-get-variant-content-options';
import { FlatList } from 'react-native';

import { centeredRowContainerStyles } from 'components/layout/centered-row-container';
import Link from 'components/link';
import { Calories } from 'components/price-and-calories';
import { Typography } from 'components/ui';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { optionToUrl } from 'utils/menu';

// OLD (not one menu)
const MenuTileGrid = React.memo<IMenuTileGridProps>(
  ({ data = [], gridTitle, pullToReloadProps, footerSpacer }) => {
    const getVariantContentOptions = useGetVariantContentOptions();
    const isMobile = useIsMobileBreakpoint();
    const getCalories = useCalories();
    return (
      <FlatList
        testID="menu-tile-grid"
        contentContainerStyle={[centeredRowContainerStyles.content, { alignSelf: 'center' }]}
        key={isMobile ? 'mobile-list' : 'desktop-list'}
        data={data}
        {...pullToReloadProps}
        ListHeaderComponent={
          <>
            {gridTitle && <Typography variant="headerThree">{gridTitle}</Typography>}
            <MenuTileGridHeader />
          </>
        }
        ItemSeparatorComponent={isMobile ? Separator : null}
        ListFooterComponent={
          isMobile ? (
            <>
              <Separator />
              {footerSpacer}
            </>
          ) : null
        }
        numColumns={isMobile ? 1 : 3}
        showsVerticalScrollIndicator={false}
        keyExtractor={mainMenuObject => mainMenuObject._id}
        renderItem={({ item }: { item: MainMenuObject }) => {
          const { image, name } = getVariantContentOptions(item);
          const linkPath = optionToUrl(item);
          const { calories, price } = getCaloriesAndPrice(
            item,
            getCalories as (menuObject: AnyMenuObject) => string
          );
          const isMenuItem = item._type !== 'section';

          let itemContent = <MenuTile objectFitContain={isMenuItem} item={item} />;

          if (!isMobile) {
            itemContent = (
              <TileCard>
                <Link
                  style={{
                    width: '100%',
                    height: '100%',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                  linkPath={linkPath}
                  accessibilityLevel={3}
                  focusable={true}
                  accessibilityRole="header"
                  accessible={true}
                >
                  <TileImage
                    image={image}
                    alt={item.imageDescription?.locale ?? 'menu item image'}
                    objectFitContain={isMenuItem}
                    importantForAccessibility="no"
                  />
                  <TileFooter isMenuItem={isMenuItem}>
                    <StyledMenuSectionTitle>{name?.locale}</StyledMenuSectionTitle>
                    {isMenuItem && (
                      <StyledPriceAndCaloriesListWrapper>
                        <StyledItemPrice>{price}</StyledItemPrice>
                        <Calories>
                          <StyledListItemCalories>{calories}</StyledListItemCalories>
                        </Calories>
                      </StyledPriceAndCaloriesListWrapper>
                    )}
                  </TileFooter>
                </Link>
              </TileCard>
            );
          }

          return itemContent;
        }}
      />
    );
  }
);

export default MenuTileGrid;
